import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { extractionFeedbackAPI } from '../../services/api';

type FeedbackRow = {
  pipeline: 'extracted_fields' | 'dhis2_stream_extractions';
  row_id: string;
  section_id: string;
  field_id: string;
  field_name: string;
  value: unknown;
  confidence?: number | null;
  is_correct?: boolean | null;
  remark?: string | null;
  correct_value?: string | null;
};

interface Props {
  open: boolean;
  onClose: () => void;
  sessionId: string | null;
}

export default function ExtractionFeedbackPanel({ open, onClose, sessionId }: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [sectionTab, setSectionTab] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'correct' | 'incorrect' | 'pending'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [ambientRating, setAmbientRating] = useState<'Bad' | 'Okay' | 'Good' | 'Excellent' | ''>('');
  const [generalFeedback, setGeneralFeedback] = useState('');

  const reviewedCount = useMemo(
    () => rows.filter((r) => r.is_correct === true || r.is_correct === false).length,
    [rows],
  );

  const grouped = useMemo(() => {
    const map: Record<string, FeedbackRow[]> = {};
    for (const r of rows) {
      if (!map[r.section_id]) map[r.section_id] = [];
      map[r.section_id].push(r);
    }
    return map;
  }, [rows]);

  const sectionOptions = useMemo(() => ['all', ...Object.keys(grouped)], [grouped]);

  const visibleRows = useMemo(() => {
    return rows.filter((r) => {
      if (sectionTab !== 'all' && r.section_id !== sectionTab) return false;
      if (statusFilter === 'correct') return r.is_correct === true;
      if (statusFilter === 'incorrect') return r.is_correct === false;
      if (statusFilter === 'pending') return r.is_correct === null || r.is_correct === undefined;
      return true;
    });
  }, [rows, sectionTab, statusFilter]);

  useEffect(() => {
    if (!open || !sessionId) return;
    setLoading(true);
    setError(null);
    setSelected(new Set());
    extractionFeedbackAPI
      .getSessionRows(sessionId)
      .then((res) =>
        {
          setRows(
            (Array.isArray(res.data?.rows) ? res.data.rows : []).map((r) => ({
            ...r,
            correct_value: null,
            })),
          );
          setAmbientRating((res.data?.ambient_experience_rating as any) ?? '');
          setGeneralFeedback(String(res.data?.general_feedback ?? ''));
        },
      )
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load extraction rows'))
      .finally(() => setLoading(false));
  }, [open, sessionId]);

  const setRowCorrect = (rowId: string, value: boolean | null) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row_id === rowId ? { ...r, is_correct: value, correct_value: value === false ? r.correct_value : null } : r,
      ),
    );
  };

  const setRowRemark = (rowId: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.row_id === rowId ? { ...r, remark: value } : r)));
  };

  const setRowCorrectValue = (rowId: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.row_id === rowId ? { ...r, correct_value: value } : r)));
  };

  const markAllVisibleCorrect = () => {
    const ids = new Set(visibleRows.map((r) => r.row_id));
    setRows((prev) =>
      prev.map((r) => (ids.has(r.row_id) ? { ...r, is_correct: true, correct_value: null } : r)),
    );
  };

  const markSelectedIncorrect = () => {
    if (!selected.size) return;
    setRows((prev) =>
      prev.map((r) =>
        selected.has(r.row_id) ? { ...r, is_correct: false, correct_value: r.correct_value ?? '' } : r,
      ),
    );
  };

  const handleSubmit = async () => {
    if (!sessionId) return;
    setSaving(true);
    setError(null);
    try {
      const updates = rows
        .filter(
          (r) =>
            r.is_correct !== undefined && r.is_correct !== null ||
            (r.remark ?? '').trim().length > 0 ||
            (r.correct_value ?? '').trim().length > 0,
        )
        .map((r) => ({
          pipeline: r.pipeline,
          row_id: r.row_id,
          section_id: r.section_id,
          field_id: r.field_id,
          is_correct: r.is_correct ?? null,
          remark:
            ([
              (r.correct_value ?? '').trim() ? `Correct value: ${(r.correct_value ?? '').trim()}` : '',
              (r.remark ?? '').trim(),
            ]
              .filter(Boolean)
              .join('\n') || null),
        }));
      await extractionFeedbackAPI.patchSessionRows(
        sessionId,
        updates,
        (ambientRating || null) as any,
        (generalFeedback || '').trim() || null,
      );
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to submit feedback');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>AI Extraction Feedback</DialogTitle>
      <DialogContent dividers>
        {!sessionId && <Alert severity="info">Start or select a session first.</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {sessionId && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Reviewed {reviewedCount} / {rows.length}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Show</InputLabel>
              <Select label="Show" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="correct">Correct</MenuItem>
                <MenuItem value="incorrect">Incorrect</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
            <Button size="small" variant="outlined" onClick={markAllVisibleCorrect}>Mark all as correct</Button>
            <Button size="small" variant="outlined" color="warning" disabled={!selected.size} onClick={markSelectedIncorrect}>
              Mark selected incorrect
            </Button>
            <Chip size="small" label={`Selected ${selected.size}`} />
          </Box>
        )}
        <Box sx={{ mb: 2, p: 1.5, border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#f8fafc' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Ambient Experience Feedback</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 340 }}>
              <InputLabel shrink sx={{ position: 'static', transform: 'none', mb: 0.75 }}>
                Ambient experience rating
              </InputLabel>
              <ToggleButtonGroup
                exclusive
                value={ambientRating || null}
                onChange={(_, v) => setAmbientRating((v ?? '') as any)}
                aria-label="Ambient experience rating"
                size="small"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 1,
                  '& .MuiToggleButtonGroup-grouped': {
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '999px !important',
                    textTransform: 'none',
                    fontWeight: 500,
                  },
                }}
              >
                <ToggleButton
                  value="Bad"
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: '#fee2e2',
                      color: '#b91c1c',
                      borderColor: '#fecaca',
                      fontWeight: 700,
                    },
                  }}
                >
                  Bad
                </ToggleButton>
                <ToggleButton
                  value="Okay"
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: '#fef9c3',
                      color: '#a16207',
                      borderColor: '#fde68a',
                      fontWeight: 700,
                    },
                  }}
                >
                  Okay
                </ToggleButton>
                <ToggleButton
                  value="Good"
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: '#dbeafe',
                      color: '#1d4ed8',
                      borderColor: '#bfdbfe',
                      fontWeight: 700,
                    },
                  }}
                >
                  Good
                </ToggleButton>
                <ToggleButton
                  value="Excellent"
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: '#dcfce7',
                      color: '#166534',
                      borderColor: '#bbf7d0',
                      fontWeight: 700,
                    },
                  }}
                >
                  Excellent
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
            <TextField
              label="General feedback"
              size="small"
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              value={generalFeedback}
              onChange={(e) => setGeneralFeedback(e.target.value)}
            />
          </Box>
        </Box>
        <Tabs
          value={sectionTab}
          onChange={(_, v) => setSectionTab(v)}
          variant="scrollable"
          sx={{ mb: 1, minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none' } }}
        >
          {sectionOptions.map((s) => (
            <Tab key={s} value={s} label={s === 'all' ? 'All sections' : s} />
          ))}
        </Tabs>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={22} />
            <Typography>Loading fields…</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 520, border: '1px solid #e5e7eb', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={visibleRows.length > 0 && visibleRows.every((r) => selected.has(r.row_id))}
                      indeterminate={visibleRows.some((r) => selected.has(r.row_id)) && !visibleRows.every((r) => selected.has(r.row_id))}
                      onChange={(_, v) =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          visibleRows.forEach((r) => (v ? next.add(r.row_id) : next.delete(r.row_id)));
                          return next;
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>AI Value</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Correct Value</TableCell>
                  <TableCell>Feedback</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((r) => (
                  <TableRow key={r.row_id} sx={r.is_correct === false ? { bgcolor: '#fff5f5' } : undefined}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.has(r.row_id)}
                        onChange={(_, v) =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (v) next.add(r.row_id);
                            else next.delete(r.row_id);
                            return next;
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.field_name || r.field_id}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.section_id}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" color="text.secondary" noWrap title={typeof r.value === 'object' ? JSON.stringify(r.value) : String(r.value ?? '')}>
                        {typeof r.value === 'object' ? JSON.stringify(r.value) : String(r.value ?? '')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: 170 }}>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={r.is_correct === true ? 'correct' : r.is_correct === false ? 'incorrect' : 'pending'}
                          onChange={(e) => {
                            const v = e.target.value;
                            setRowCorrect(r.row_id, v === 'pending' ? null : v === 'correct');
                          }}
                        >
                          <MenuItem value="correct">Correct</MenuItem>
                          <MenuItem value="incorrect">Incorrect</MenuItem>
                          <MenuItem value="pending">Pending</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <TextField
                        size="small"
                        fullWidth
                        disabled={r.is_correct !== false}
                        placeholder={r.is_correct === false ? 'Enter correct value' : 'Enable by setting Incorrect'}
                        value={r.correct_value ?? ''}
                        onChange={(e) => setRowCorrectValue(r.row_id, e.target.value)}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={3}
                        placeholder="Optional feedback"
                        value={r.remark ?? ''}
                        onChange={(e) => setRowRemark(r.row_id, e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {!visibleRows.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">No rows for current filters.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!sessionId || saving || loading}>
          {saving ? 'Submitting…' : 'Submit feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
